import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { Table } from "antd";
import dayjs from "dayjs";
import { BarChart2, Users } from "react-feather";
import InfoTile from "../../../components/common/InfoTile";
import PrimaryCell from "../../../components/common/PrimaryCell";
import ListExportToolbar from "../../stock/components/list/ListExportToolbar";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import {
  useGetCollectorOptionsQuery,
  useGetCollectorStatsQuery,
  useGetCollectorPerformanceQuery,
  useGetCollectorPerformanceCustomersQuery,
  useGetCollectorPerformanceDetailQuery,
  money, tidy, customerKey, sinceLabel,
} from "../collectorListsApi";
import { paths } from "../../../routes/paths";

/*
  Collector Performance (Batch189) - how each collector did against the list of
  customers they own (see Collector Lists).

  For a month, a customer on a collector's list is exactly one of:
    collected            - this collector collected from them
    collected by others  - somebody else did (the money came in, but not by them)
    missed               - nobody did
  Every customer is expected every month - the user's rule, no per-customer
  cycle - so a quarterly payer shows as missed in the months between. That is
  intended; it is the list that should change, not the report.

  Beside the list figures, each collector also shows what they collected from
  customers that are NOT theirs: on somebody else's list, or on nobody's.
*/

const MAROON = "#6E1423";

// The web holds collections from August 2026 only, so the report never looks
// earlier - before that every customer would read as "missed" for months
// nobody could have recorded here. The API clamps to the same month.
const FIRST_MONTH = "2026-08";
const notBefore = (ym) => (ym && ym < FIRST_MONTH ? FIRST_MONTH : ym);

// The customer-names list (Batch190): one row per customer + month + category.
const CATEGORY = {
  COLLECTED: { label: "Collected", cls: "badge-linesuccess" },
  MISSED: { label: "Not collected", cls: "badge-linedanger" },
  COLLECTED_BY_OTHER: { label: "Collected by someone else", cls: "badge-linewarning" },
  NOT_THEIR_CUSTOMER: { label: "Cross collected - another's customer", cls: "badge-lineinfo" },
  NO_COLLECTOR: { label: "Cross collected - no collector", cls: "badge-linesecondary" },
};
const CATEGORY_ORDER = ["COLLECTED", "MISSED", "COLLECTED_BY_OTHER", "NOT_THEIR_CUSTOMER", "NO_COLLECTOR"];
const monthLabel = (d) => dayjs(d).format("MMM YYYY");
const monthParam = (ym) => (ym ? `${ym}-01` : undefined);
const rate = (collected, onList) => (onList > 0 ? `${((collected / onList) * 100).toFixed(1)}%` : "-");

const selectStyles = {
  container: (base) => ({ ...base, minWidth: 280 }),
  control: (base) => ({ ...base, minHeight: 34 }),
  menu: (base) => ({ ...base, zIndex: 20 }),
};

const CUSTOMER_FILTERS = [
  { value: "ALL", label: "All" },
  { value: "MISSED", label: "Missed in this range" },
  { value: "NEVER", label: "Never collected" },
];

const StatusCell = ({ cell }) => {
  if (!cell || cell.monthStatus === "FUTURE") return <span className="text-muted">-</span>;
  if (cell.monthStatus === "COLLECTED") {
    return <span style={{ color: "#1E7E34", fontWeight: 600 }} title={`${cell.collectedCount} collection(s)`}>✓ {money(cell.collectedAmount)}</span>;
  }
  if (cell.monthStatus === "COLLECTED_OTHER") {
    return <span style={{ color: "#B7791F" }} title="Collected this month, but by somebody else">other</span>;
  }
  return <span className="text-danger fw-semibold">missed</span>;
};

const CollectorPerformancePage = () => {
  const navigate = useNavigate();
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const [monthFrom, setMonthFrom] = useState(notBefore(dayjs().subtract(2, "month").format("YYYY-MM")));
  const [monthTo, setMonthTo] = useState(dayjs().format("YYYY-MM"));
  const [collector, setCollector] = useState(null);
  const [detailCollector, setDetailCollector] = useState(null);
  const [customerFilter, setCustomerFilter] = useState("ALL");
  // Set by clicking a red "Missed" figure: narrows the customer list to the
  // ones missed in THAT month (null = missed in any month of the range).
  const [missedMonth, setMissedMonth] = useState(null);
  const detailRef = useRef(null);
  // Names list filters: which figure was clicked, and for which month.
  const [nameCategory, setNameCategory] = useState("ALL");
  const [nameMonth, setNameMonth] = useState("");
  const [nameSearch, setNameSearch] = useState("");

  // Reported 2026-09-21: "how do we know which customers are not collected and
  // for which months". The answer was already on the page, but it opens BELOW a
  // long table, off screen, so a click looked like it did nothing. Opening a
  // collector now scrolls their customer list into view.
  // Every figure in the first table is a link into the names list: the
  // category it stands for, and its month (the total row = every month).
  const openCollector = (guid, { category, month } = {}) => {
    setDetailCollector(guid);
    setNameCategory(category || "ALL");
    setNameMonth(month || "");
    setNameSearch("");
  };

  const branchId = selectedBranchId ?? undefined;
  const branchCode = availableBranches.find((b) => b.branchId === selectedBranchId)?.branchCode;
  const range = { monthFrom: monthParam(monthFrom), monthTo: monthParam(monthTo) };

  const { data: options = [] } = useGetCollectorOptionsQuery({ branchId });
  const { data: stats } = useGetCollectorStatsQuery({ branchId });
  const { data: rows = [], isFetching, isError, error } = useGetCollectorPerformanceQuery(
    { branchId, ...range, collector: collector?.value },
    { skip: !monthFrom || !monthTo },
  );

  // Picking a collector in the filter is the same as asking for their customers.
  const detailGuid = collector?.value || detailCollector;
  const { data: customerRows = [], isFetching: isFetchingCustomers } = useGetCollectorPerformanceCustomersQuery(
    { branchId, ...range, collector: detailGuid },
    { skip: !detailGuid || !monthFrom || !monthTo },
  );

  const { data: detailRows = [], isFetching: isFetchingDetail } = useGetCollectorPerformanceDetailQuery(
    { branchId, ...range, collector: detailGuid },
    { skip: !detailGuid || !monthFrom || !monthTo },
  );

  const collectorOptions = useMemo(
    () => options.map((o) => ({ value: o.employeeRecordGuid, label: `${tidy(o.empName)} · ${o.empCode} · ${o.branchCode}` })),
    [options],
  );

  // ---- Table A: group heading + month rows + subtotal, per collector ----
  const grouped = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      if (!map.has(r.collectorEmployeeRecordGuid)) {
        map.set(r.collectorEmployeeRecordGuid, {
          guid: r.collectorEmployeeRecordGuid, name: tidy(r.collectorName) || "(unknown)",
          code: r.collectorEmpCode, branch: r.collectorBranchCode, months: [],
        });
      }
      map.get(r.collectorEmployeeRecordGuid).months.push(r);
    });
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const sumOf = (list) =>
    list.reduce(
      (t, r) => ({
        // Customer counts are per month and the same customers repeat, so a
        // subtotal adds customer-MONTHS - labelled as such in the heading.
        onList: t.onList + r.onList, collected: t.collected + r.collected,
        collectedByOthers: t.collectedByOthers + r.collectedByOthers, missed: t.missed + r.missed,
        ownAmount: t.ownAmount + Number(r.ownAmount || 0),
        offListCount: t.offListCount + r.offListCount, offListAmount: t.offListAmount + Number(r.offListAmount || 0),
        unallocatedCount: t.unallocatedCount + r.unallocatedCount, unallocatedAmount: t.unallocatedAmount + Number(r.unallocatedAmount || 0),
      }),
      { onList: 0, collected: 0, collectedByOthers: 0, missed: 0, ownAmount: 0, offListCount: 0, offListAmount: 0, unallocatedCount: 0, unallocatedAmount: 0 },
    );

  const tableA = useMemo(
    () =>
      grouped.flatMap((g) => [
        { rowKey: `g-${g.guid}`, __group: true, guid: g.guid, label: `${g.name} · ${g.code ?? ""} · ${g.branch ?? ""}` },
        ...g.months.map((m) => ({ ...m, rowKey: `${g.guid}-${m.monthStart}`, guid: g.guid })),
        ...(g.months.length > 1 ? [{ rowKey: `t-${g.guid}`, __total: true, guid: g.guid, ...sumOf(g.months) }] : []),
      ]),
    [grouped],
  );

  // ---- Tiles: list figures for the LATEST month only (adding months would
  // count the same customer several times); money for the whole range. ----
  const tiles = useMemo(() => {
    const latest = rows.reduce((m, r) => (r.monthStart > m ? r.monthStart : m), "");
    const last = sumOf(rows.filter((r) => r.monthStart === latest));
    const all = sumOf(rows);
    return { latest, ...last, ownAmountAll: all.ownAmount, strayAll: all.offListCount + all.unallocatedCount };
  }, [rows]);

  // ---- Table B: pivot customer x month ----
  const months = useMemo(() => [...new Set(customerRows.map((r) => r.monthStart))].sort(), [customerRows]);

  const customers = useMemo(() => {
    const map = new Map();
    customerRows.forEach((r) => {
      const key = customerKey(r);
      if (!map.has(key)) {
        map.set(key, {
          key, custId: r.custId, custName: tidy(r.custName), branchCode: r.customerBranchCode,
          lastCollectedDate: r.lastCollectedDate, monthsSince: r.monthsSinceLastCollected,
          cells: {}, collectedMonths: 0, missedMonths: 0,
        });
      }
      const c = map.get(key);
      c.cells[r.monthStart] = r;
      if (r.monthStatus === "COLLECTED") c.collectedMonths += 1;
      if (r.monthStatus === "MISSED") c.missedMonths += 1;
    });
    // Longest-uncollected first; "never" is the longest of all.
    const weight = (c) => (c.monthsSince == null ? Number.MAX_SAFE_INTEGER : c.monthsSince);
    return [...map.values()]
      .filter((c) =>
        customerFilter === "MISSED"
          ? (missedMonth ? c.cells[missedMonth]?.monthStatus === "MISSED" : c.missedMonths > 0)
          : customerFilter === "NEVER" ? c.lastCollectedDate == null : true)
      .sort((a, b) => weight(b) - weight(a) || a.custName.localeCompare(b.custName));
  }, [customerRows, customerFilter, missedMonth]);

  useEffect(() => {
    if (detailCollector && detailRef.current) detailRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [detailCollector, nameCategory, nameMonth]);

  // ---- Names list ----
  const nameMonths = useMemo(() => [...new Set(detailRows.map((r) => r.monthStart))].sort().reverse(), [detailRows]);
  const inMonthAndSearch = useMemo(() => {
    const needle = nameSearch.trim().toLowerCase();
    return detailRows.filter(
      (r) =>
        (!nameMonth || r.monthStart === nameMonth) &&
        (!needle || tidy(r.custName).toLowerCase().includes(needle) || String(r.custId).includes(needle)),
    );
  }, [detailRows, nameMonth, nameSearch]);
  // Chip figures ignore the category filter itself, so picking one never
  // zeroes the others.
  const categoryTotals = useMemo(() => {
    const t = { ALL: { n: inMonthAndSearch.length, amount: 0 } };
    CATEGORY_ORDER.forEach((c) => { t[c] = { n: 0, amount: 0 }; });
    inMonthAndSearch.forEach((r) => {
      if (!t[r.category]) return;
      t[r.category].n += 1;
      t[r.category].amount += Number(r.amount || 0);
      t.ALL.amount += Number(r.amount || 0);
    });
    return t;
  }, [inMonthAndSearch]);
  const nameRows = useMemo(
    () => (nameCategory === "ALL" ? inMonthAndSearch : inMonthAndSearch.filter((r) => r.category === nameCategory)),
    [inMonthAndSearch, nameCategory],
  );

  const detailName = useMemo(() => {
    const o = options.find((x) => x.employeeRecordGuid === detailGuid);
    return o ? tidy(o.empName) : grouped.find((g) => g.guid === detailGuid)?.name || "";
  }, [options, grouped, detailGuid]);

  const filterLines = [
    `Branch: ${branchCode || "All"}`,
    `Months: ${monthLabel(monthParam(monthFrom))} to ${monthLabel(monthParam(monthTo))}`,
    collector ? `Collector: ${collector.label}` : null,
  ].filter(Boolean);

  const num = (v, danger = false) => (
    <span className={v ? (danger ? "text-danger fw-semibold" : "") : "text-muted"} style={{ fontVariantNumeric: "tabular-nums" }}>{v}</span>
  );
  // A figure that opens the customer names behind it.
  const figure = (count, r, category, amount) =>
    count ? (
      <button
        type="button"
        className="btn btn-link p-0 text-decoration-underline"
        style={{ fontVariantNumeric: "tabular-nums" }}
        title="Show the customer names"
        onClick={(e) => { e.stopPropagation(); openCollector(r.guid, { category, month: r.__total ? "" : r.monthStart }); }}
      >
        {count}
        {amount != null && <span className="text-muted small ms-1">({money(amount)})</span>}
      </button>
    ) : <span className="text-muted">0</span>;

  const colCount = 9;
  const columnsA = [
    {
      title: "Month",
      dataIndex: "monthStart",
      render: (v, r) =>
        r.__group ? <div className="fw-bold" style={{ color: MAROON }}>{r.label}</div>
        : r.__total ? <strong>Total (customer-months)</strong>
        : monthLabel(v),
    },
    { title: "On list", dataIndex: "onList", align: "right", render: (v) => num(v) },
    { title: "Collected", dataIndex: "collected", align: "right", render: (v, r) => figure(v, r, "COLLECTED") },
    { title: "Collected by others", dataIndex: "collectedByOthers", align: "right", render: (v, r) => figure(v, r, "COLLECTED_BY_OTHER") },
    {
      title: "Missed",
      dataIndex: "missed",
      align: "right",
      // The red figure is the question people come here with, so it is the
      // link: it opens exactly the customers missed in that month (or, on the
      // total row, in any month of the range).
      render: (v, r) =>
        v ? (
          <button
            type="button"
            className="btn btn-link p-0 text-danger fw-semibold text-decoration-underline"
            style={{ fontVariantNumeric: "tabular-nums" }}
            title="Show which customers were missed"
            onClick={(e) => { e.stopPropagation(); openCollector(r.guid, { category: "MISSED", month: r.__total ? "" : r.monthStart }); }}
          >
            {v}
          </button>
        ) : num(v, true),
    },
    { title: "Collection rate", dataIndex: "rate", align: "right", render: (_, r) => rate(r.collected, r.onList) },
    { title: "Amount (own list)", dataIndex: "ownAmount", align: "right", render: (v) => money(v) },
    { title: "Not their customers", dataIndex: "offListCount", align: "right", render: (v, r) => figure(v, r, "NOT_THEIR_CUSTOMER", r.offListAmount) },
    { title: "Customers with no collector", dataIndex: "unallocatedCount", align: "right", render: (v, r) => figure(v, r, "NO_COLLECTOR", r.unallocatedAmount) },
  ].map((c, i) => ({ ...c, onCell: (r) => (r.__group ? { colSpan: i === 0 ? colCount : 0 } : {}) }));

  const exportA = [
    { header: "Collector", value: (r) => tidy(r.collectorName) },
    { header: "Code", value: (r) => r.collectorEmpCode ?? "" },
    { header: "Month", value: (r) => monthLabel(r.monthStart) },
    { header: "On list", value: (r) => r.onList, align: "right" },
    { header: "Collected", value: (r) => r.collected, align: "right" },
    { header: "Collected by others", value: (r) => r.collectedByOthers, align: "right" },
    { header: "Missed", value: (r) => r.missed, align: "right" },
    { header: "Rate", value: (r) => rate(r.collected, r.onList), align: "right" },
    { header: "Amount (own list)", value: (r) => Number(r.ownAmount || 0), align: "right" },
    { header: "Not their customers", value: (r) => r.offListCount, align: "right" },
    { header: "Not theirs amount", value: (r) => Number(r.offListAmount || 0), align: "right" },
    { header: "No-collector customers", value: (r) => r.unallocatedCount, align: "right" },
    { header: "No-collector amount", value: (r) => Number(r.unallocatedAmount || 0), align: "right" },
  ];

  const columnsB = [
    {
      title: "Customer",
      dataIndex: "custName",
      fixed: "left",
      render: (v, c) => <PrimaryCell primary={v || `Customer ${c.custId}`} secondary={`#${c.custId} · ${c.branchCode}`} />,
    },
    ...months.map((m) => ({ title: monthLabel(m), dataIndex: m, align: "right", render: (_, c) => <StatusCell cell={c.cells[m]} /> })),
    { title: "Collected", dataIndex: "collectedMonths", align: "right", render: (v) => num(v) },
    { title: "Missed", dataIndex: "missedMonths", align: "right", render: (v) => num(v, true) },
    {
      title: "Last collected",
      dataIndex: "lastCollectedDate",
      render: (v) => (v ? dayjs(v).format("DD-MMM-YYYY") : <span className="text-muted">never</span>),
    },
    {
      title: "Not collected for",
      dataIndex: "monthsSince",
      align: "right",
      render: (v) => <span className={v == null || v >= 2 ? "text-danger fw-semibold" : ""}>{sinceLabel(v)}</span>,
    },
  ];

  const columnsNames = [
    { title: "Month", dataIndex: "monthStart", width: 110, render: (v) => monthLabel(v) },
    {
      title: "Customer",
      dataIndex: "custName",
      render: (v, r) => <PrimaryCell primary={tidy(v) || `Customer ${r.custId}`} secondary={`#${r.custId} · ${r.customerBranchCode}`} />,
    },
    {
      title: "Status",
      dataIndex: "category",
      render: (v) => <span className={`badge ${CATEGORY[v]?.cls || "badge-linesecondary"}`}>{CATEGORY[v]?.label || v}</span>,
    },
    { title: "Collections", dataIndex: "collections", align: "right", render: (v) => num(v) },
    { title: "Amount", dataIndex: "amount", align: "right", render: (v, r) => (r.category === "MISSED" ? <span className="text-muted">-</span> : money(v)) },
    {
      title: "Other collector",
      dataIndex: "otherCollectorName",
      render: (v, r) =>
        v ? (
          <>
            {tidy(v)} <span className="text-muted small">· {r.otherCollectorEmpCode}</span>
            <div className="text-muted small">{r.category === "COLLECTED_BY_OTHER" ? "collected it" : "owns this customer"}</div>
          </>
        ) : <span className="text-muted">-</span>,
    },
  ];

  const exportNames = [
    { header: "Month", value: (r) => monthLabel(r.monthStart) },
    { header: "Customer", value: (r) => tidy(r.custName) },
    { header: "Cust No.", value: (r) => r.custId },
    { header: "Branch", value: (r) => r.customerBranchCode },
    { header: "Status", value: (r) => CATEGORY[r.category]?.label || r.category },
    { header: "Collections", value: (r) => r.collections, align: "right" },
    { header: "Amount", value: (r) => Number(r.amount || 0), align: "right" },
    { header: "Other collector", value: (r) => tidy(r.otherCollectorName) },
  ];

  const cellText = (cell) =>
    !cell || cell.monthStatus === "FUTURE" ? ""
    : cell.monthStatus === "COLLECTED" ? `Collected ${money(cell.collectedAmount)}`
    : cell.monthStatus === "COLLECTED_OTHER" ? "Other" : "Missed";

  const exportB = [
    { header: "Customer", value: (c) => c.custName },
    { header: "Cust No.", value: (c) => c.custId },
    { header: "Branch", value: (c) => c.branchCode },
    ...months.map((m) => ({ header: monthLabel(m), value: (c) => cellText(c.cells[m]) })),
    { header: "Collected", value: (c) => c.collectedMonths, align: "right" },
    { header: "Missed", value: (c) => c.missedMonths, align: "right" },
    { header: "Last collected", value: (c) => (c.lastCollectedDate ? dayjs(c.lastCollectedDate).format("DD-MMM-YYYY") : "never") },
    { header: "Not collected for", value: (c) => sinceLabel(c.monthsSince) },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Collector Performance</h4>
              <h6>How each collector did against their monthly customer list</h6>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body d-flex flex-wrap align-items-center gap-2">
            <label className="small text-muted mb-0">From</label>
            <input type="month" className="form-control form-control-sm" style={{ width: 170, height: 34 }} value={monthFrom} min={FIRST_MONTH} max={monthTo} onChange={(e) => setMonthFrom(notBefore(e.target.value))} />
            <label className="small text-muted mb-0">To</label>
            <input type="month" className="form-control form-control-sm" style={{ width: 170, height: 34 }} value={monthTo} min={monthFrom || FIRST_MONTH} onChange={(e) => setMonthTo(notBefore(e.target.value))} />
            <small className="text-muted">Records start August 2026.</small>
            <Select
              classNamePrefix="react-select"
              styles={selectStyles}
              isClearable
              options={collectorOptions}
              value={collector}
              onChange={(v) => { setCollector(v); setDetailCollector(null); }}
              placeholder="All collectors - type to search"
            />
          </div>
        </div>

        {isError && <div className="alert alert-danger">Could not load the report ({error?.message || "server error"}).</div>}

        <div className="row g-2 mb-1">
          <div className="col-6 col-md-4 col-xl-2"><InfoTile label="On lists" value={tiles.onList} /></div>
          <div className="col-6 col-md-4 col-xl-2"><InfoTile label="Collected" tone="success" value={tiles.collected} /></div>
          <div className="col-6 col-md-4 col-xl-2"><InfoTile label="Collected by someone else" value={tiles.collectedByOthers} /></div>
          <div className="col-6 col-md-4 col-xl-2"><InfoTile label="Missed" tone={tiles.missed ? "maroon" : "neutral"} value={tiles.missed} /></div>
          <div className="col-6 col-md-4 col-xl-2"><InfoTile label="Amount (own lists, whole range)" value={money(tiles.ownAmountAll)} /></div>
          <div className="col-6 col-md-4 col-xl-2">
            <InfoTile
              label="Customers with no collector"
              tone={stats?.unallocatedCustomers ? "maroon" : "neutral"}
              value={stats?.unallocatedCustomers ?? "—"}
              onClick={() => navigate(`${paths.collectorLists}?unallocated=1`)}
              title="Collected from in the last 12 months but on nobody's list. Click to open them."
            />
          </div>
        </div>
        <small className="text-muted d-block mb-3">
          The first four figures are for {tiles.latest ? monthLabel(tiles.latest) : "the latest month"} only - adding months together would count the same customer more than once.
        </small>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
              <h5 className="mb-0 d-inline-flex align-items-center gap-2"><BarChart2 size={18} /> By collector and month</h5>
              <ListExportToolbar
                title="Collector Performance"
                subtitleLines={filterLines}
                branchCode={branchCode}
                columns={exportA}
                rows={rows}
                groupBy={(r) => tidy(r.collectorName)}
                orientation="landscape"
                fileName={`CollectorPerformance_${monthTo}`}
              />
            </div>
            <div className="table-responsive">
              <Table
                className="table datanew dataTable no-footer"
                size="small"
                columns={columnsA}
                dataSource={tableA}
                rowKey="rowKey"
                loading={isFetching}
                pagination={false}
                locale={{ emptyText: "No collector has a list or a collection in these months" }}
                rowClassName={(r) => (r.__group ? "bg-light" : "")}
                onRow={(r) => ({
                  onClick: () => openCollector(r.guid),
                  style: {
                    cursor: "pointer",
                    fontWeight: r.__total ? 700 : undefined,
                    background: detailGuid === r.guid && !r.__group ? "#F7ECEE" : undefined,
                  },
                })}
              />
            </div>
            <small className="text-muted d-block mt-2">
              <strong>Click any figure to see the customer names behind it</strong> - collected, not collected or
              cross collected, with the amount and month. The list opens below.
              &ldquo;Not their customers&rdquo; is what they
              collected from customers on somebody else&rsquo;s list; &ldquo;no collector&rdquo; is from customers on
              nobody&rsquo;s list.
            </small>
          </div>
        </div>

        {detailGuid && (
          <div className="card table-list-card" ref={detailRef} style={{ scrollMarginTop: 80 }}>
            <div className="card-body">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                <h5 className="mb-0 d-inline-flex align-items-center gap-2"><Users size={18} /> Customer names - {detailName}</h5>
                <ListExportToolbar
                  title={`Collector Customers by Status - ${detailName}`}
                  subtitleLines={[
                    ...filterLines,
                    nameCategory !== "ALL" ? `Showing: ${CATEGORY[nameCategory].label}` : null,
                    nameMonth ? `Month: ${monthLabel(nameMonth)}` : null,
                  ].filter(Boolean)}
                  branchCode={branchCode}
                  columns={exportNames}
                  rows={nameRows}
                  groupBy={(r) => CATEGORY[r.category]?.label || r.category}
                  orientation="landscape"
                  fileName={`CollectorCustomerNames_${monthTo}`}
                />
              </div>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <div className="btn-group btn-group-sm flex-wrap" role="group" aria-label="Which customers to list">
                  {["ALL", ...CATEGORY_ORDER].map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={`btn ${nameCategory === c ? "btn-primary" : "btn-outline-secondary"}`}
                      onClick={() => setNameCategory(c)}
                      title={c === "ALL" ? undefined : `${money(categoryTotals[c].amount)}`}
                    >
                      {c === "ALL" ? "All" : CATEGORY[c].label} <span className="ms-1 opacity-75">{categoryTotals[c].n}</span>
                    </button>
                  ))}
                </div>
                <select className="form-select form-select-sm" style={{ width: 160 }} value={nameMonth} onChange={(e) => setNameMonth(e.target.value)}>
                  <option value="">All months</option>
                  {nameMonths.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
                </select>
                <input
                  type="text" className="form-control form-control-sm" style={{ width: 220, height: 31 }}
                  placeholder="Customer name or number" value={nameSearch} onChange={(e) => setNameSearch(e.target.value)}
                />
                <span className="ms-auto small">
                  {nameRows.length} row(s) &middot; <strong>{money(nameRows.reduce((t, r) => t + Number(r.amount || 0), 0))}</strong>
                </span>
              </div>
              <div className="table-responsive">
                <Table
                  className="table datanew dataTable no-footer"
                  size="small"
                  columns={columnsNames}
                  dataSource={nameRows}
                  rowKey={(r) => `${r.category}-${r.monthStart}-${customerKey(r)}-${r.otherCollectorEmployeeRecordGuid || ""}`}
                  loading={isFetchingDetail}
                  locale={{ emptyText: "No customers match" }}
                  pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: [25, 50, 100, 200] }}
                />
              </div>
              <small className="text-muted d-block mt-2">
                &ldquo;Cross collected&rdquo; is money this collector took from a customer that is not on their list:
                either another collector&rsquo;s customer (named in the last column) or a customer with no collector.
                &ldquo;Collected by someone else&rdquo; is the reverse - their customer, collected by the person named.
              </small>
            </div>
          </div>
        )}

        {detailGuid && (
          <div className="card table-list-card">
            <div className="card-body">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                <h5 className="mb-0 d-inline-flex align-items-center gap-2"><Users size={18} /> Customers of {detailName}</h5>
                <ListExportToolbar
                  title={`Collector Customers - ${detailName}`}
                  subtitleLines={filterLines}
                  branchCode={branchCode}
                  columns={exportB}
                  rows={customers}
                  orientation="landscape"
                  fileName={`CollectorCustomers_${monthTo}`}
                />
              </div>
              <div className="btn-group btn-group-sm mb-2" role="group" aria-label="Which customers to show">
                {CUSTOMER_FILTERS.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    className={`btn ${customerFilter === o.value ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => { setCustomerFilter(o.value); setMissedMonth(null); }}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              {customerFilter === "MISSED" && missedMonth && (
                <span className="badge badge-linedanger ms-2 align-middle">
                  missed in {monthLabel(missedMonth)} &middot; {customers.length} customer(s){" "}
                  <button type="button" className="btn btn-link btn-sm p-0 ms-1" onClick={() => setMissedMonth(null)}>any month</button>
                </span>
              )}
              <div className="table-responsive">
                <Table
                  className="table datanew dataTable no-footer"
                  size="small"
                  columns={columnsB}
                  dataSource={customers}
                  rowKey="key"
                  loading={isFetchingCustomers}
                  locale={{ emptyText: "No customers match" }}
                  pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: [25, 50, 100, 200] }}
                />
              </div>
              <small className="text-muted d-block mt-2">
                Longest-uncollected first. A dash means the customer was not on this collector&rsquo;s list that month.
              </small>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectorPerformancePage;
